import appConfig from '../../configs/appConfig';
import envVariables from '../../consts/envVariables';
import iapClientService from '../../services/iap/iapClientService';
import {selectUwmModules} from '../../services/iap/iapMappingService';
import log from '../logger/log';
import ConfigManager from './ConfigManager';

const configManager = new ConfigManager();

export const getConfig = () => configManager.getConfig();

const initializeConfig = async () => configManager.initialize();

const updateConfig = (config) => configManager.mergeConfig(config);

const handleGeneralConfig = async () => {
    try {
        const generalConfig = await iapClientService.getIapGeneralConfig();

        updateConfig(generalConfig);
        configManager.saveBase();
    } catch {
        log.error('configService: Failed to get general config');
    }
};

const resetConfig = () => configManager.reset();

const fetchGeneralConfigForSWCache = async () => {
    try {
        await iapClientService.getIapGeneralConfig();
    } catch {
        log.error('configService: Failed to fetch general config for SW cache');
    }
};

const getMarketConfig = async () => {
    try {
        return await iapClientService.getIapMarketConfig();
    } catch {
        log.error('configService: Failed to get market config');
    }
};

const getUserGroupFeatures = async () => {
    const userGroup = appConfig.getUserGroup();

    if (!userGroup) {
        log.warn('configService: No user group resolved, skipping user groups config');
        return [];
    }

    try {
        const userGroupsConfig = await iapClientService.getIapUserGroupsConfig();
        const groupConfig = userGroupsConfig?.find(
            ({appType, group}) => appType === envVariables.APP_TYPE && group === userGroup
        );

        if (!groupConfig) log.warn(`configService: No user groups config entry for group "${userGroup}"`);

        return groupConfig?.features ?? [];
    } catch {
        log.error('configService: Failed to get user groups config');

        return [];
    }
};

const resolveSettings = (marketSettings = {}, userGroupFeatures) =>
    Object.fromEntries(
        Object.entries(marketSettings).map(([code, value]) => [
            code,
            Boolean(value) && userGroupFeatures.includes(code),
        ])
    );

const getTargetGroupsUwmConfig = async (targetGroups) => {
    if (!targetGroups?.length) {
        log.warn('configService: No target groups resolved, skipping target groups config');
        return;
    }

    try {
        const targetGroupsConfig = await iapClientService.getIapTargetGroupsConfig();
        const groupConfigs = targetGroupsConfig?.filter(
            ({appType, targetGroup}) => appType === envVariables.APP_TYPE && targetGroups.includes(targetGroup)
        );

        if (!groupConfigs?.length) {
            log.info(`configService: No target groups config entry for groups ${JSON.stringify(targetGroups)}`);
            return;
        }

        return selectUwmModules(groupConfigs.flatMap(({uwmConfig}) => uwmConfig ?? []));
    } catch {
        log.error('configService: Failed to get target groups config');
    }
};

const handleSessionConfig = async (targetGroups) => {
    const [marketConfig, userGroupFeatures, targetGroupsUwmConfig] = await Promise.all([
        getMarketConfig(),
        getUserGroupFeatures(),
        getTargetGroupsUwmConfig(targetGroups),
    ]);

    updateConfig({...marketConfig, settings: resolveSettings(marketConfig?.settings, userGroupFeatures)});
    updateConfig({uwmConfig: targetGroupsUwmConfig});
};

export default {
    initializeConfig,
    updateConfig,
    resetConfig,
    handleGeneralConfig,
    fetchGeneralConfigForSWCache,
    handleSessionConfig,
};
